import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  List,
  Stack,
  Text,
} from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';

import {
  FoundingCohortCounter,
  useCohortPosition,
} from '../components/marketing/FoundingCohortCounter';
import { MarketingHero } from '../components/marketing/MarketingHero';
import { MarketingNextSteps } from '../components/marketing/MarketingNextSteps';
import { MarketingSection } from '../components/marketing/MarketingSection';
import { PublicShell } from '../components/marketing/PublicShell';
import { JoinLink } from '../components/ui/JoinLink';
import { useCmsImage, useCmsSlot } from '../lib/cms';
import { shouldShowFoundingCohortCount } from '../lib/founding-cohort-reveal';
import { formatAud, usePricing } from '../lib/pricing';

export const Route = createFileRoute('/founding-venues')({
  component: FoundingVenueOfferPage,
});

function FoundingVenueOfferPage() {
  const cohort = useCohortPosition();
  const pricing = usePricing();
  const claimed = cohort.data?.claimed ?? 0;
  const cap = cohort.data?.cap ?? 50;
  const remaining = Math.max(0, cap - claimed);
  const fullyClaimed = cohort.data !== undefined && claimed >= cap;
  const closingSoon = cohort.data !== undefined && claimed >= cap - 10 && claimed < cap;

  // Pricing is admin-tunable via /admin/settings; placeholderData keeps the
  // marketing page from flashing empty on first paint.
  const monthlyAud = pricing.data?.monthly ?? 29;
  const yearlyAud = pricing.data?.yearly ?? 290;
  const yearlySaveAud = monthlyAud * 12 - yearlyAud;
  const yearlySavePct =
    monthlyAud * 12 > 0 ? Math.round((yearlySaveAud / (monthlyAud * 12)) * 100) : 0;

  // Wave C — hero copy is CMS slots.
  const heroHeadline = useCmsSlot<'markdown'>('founding_venue_offer.headline');
  const heroLede = useCmsSlot<'markdown'>('founding_venue_offer.lede');
  const { image: heroImage, ready: heroReady } = useCmsImage('founding_venue_offer.hero_image');

  // Hero eyebrow: one of four cohort-status branches, CMS-editable.
  const eyebrowDefault = useCmsSlot<'markdown'>('founding_venue_offer.eyebrow_default');
  const eyebrowClosingSoon = useCmsSlot<'markdown'>('founding_venue_offer.eyebrow_closing_soon');
  const eyebrowFull = useCmsSlot<'markdown'>('founding_venue_offer.eyebrow_full');
  const eyebrowLoading = useCmsSlot<'markdown'>('founding_venue_offer.eyebrow_loading');
  const eyebrowOpen = useCmsSlot<'markdown'>('founding_venue_offer.eyebrow_open');

  // Section headers + body copy are CMS slots.
  const offerEyebrow = useCmsSlot<'markdown'>('founding_venue_offer.offer_eyebrow');
  const offerTitle = useCmsSlot<'markdown'>('founding_venue_offer.offer_title');
  const offerItems = useCmsSlot<'step_list'>('founding_venue_offer.offer_items');
  const mathEyebrow = useCmsSlot<'markdown'>('founding_venue_offer.math_eyebrow');
  const mathTitle = useCmsSlot<'markdown'>('founding_venue_offer.math_title');
  const mathPromise = useCmsSlot<'markdown'>('founding_venue_offer.math_promise');
  const referralCallout = useCmsSlot<'markdown'>('founding_venue_offer.referral_callout');
  const priceCaptionYearOne = useCmsSlot<'markdown'>(
    'founding_venue_offer.price_caption_year_one',
  );
  const priceCaptionMonthly = useCmsSlot<'markdown'>('founding_venue_offer.price_caption_monthly');
  const priceCaptionYearlySaves = useCmsSlot<'markdown'>(
    'founding_venue_offer.price_caption_yearly_saves',
  );
  const priceCaptionYearlyPlain = useCmsSlot<'markdown'>(
    'founding_venue_offer.price_caption_yearly_plain',
  );
  const reciprocityEyebrow = useCmsSlot<'markdown'>('founding_venue_offer.reciprocity_eyebrow');
  const reciprocityTitle = useCmsSlot<'markdown'>('founding_venue_offer.reciprocity_title');
  const reciprocityList = useCmsSlot<'bullet_list'>('founding_venue_offer.reciprocity_list');
  const ctaButtonDefault = useCmsSlot<'markdown'>('founding_venue_offer.cta_button_default');
  const ctaButtonClaimed = useCmsSlot<'markdown'>('founding_venue_offer.cta_button_claimed');
  const ctaClaimedNote = useCmsSlot<'markdown'>('founding_venue_offer.cta_claimed_note');
  const nextStepsTitle = useCmsSlot<'markdown'>('founding_venue_offer.next_steps_title');

  // The yearly caption swaps between a savings and a no-savings variant; the
  // {save} placeholder keeps the live percentage in the admin-edited copy.
  const yearlyCaption =
    yearlySavePct > 0
      ? priceCaptionYearlySaves.replace(/\{save\}/g, String(yearlySavePct))
      : priceCaptionYearlyPlain;
  // {cap} / {monthly} placeholders keep the live cohort size + price in the
  // admin-edited CTA copy.
  const claimedNote = ctaClaimedNote.replace(/\{cap\}/g, String(cap));

  const eyebrow = (
    fullyClaimed
      ? eyebrowFull
      : closingSoon
        ? eyebrowClosingSoon
        : cohort.data === undefined
          ? eyebrowLoading
          : shouldShowFoundingCohortCount(claimed)
            ? eyebrowDefault
            : eyebrowOpen
  )
    .replace(/\{remaining\}/g, String(remaining))
    .replace(/\{cap\}/g, String(cap))
    .replace(/\{spot_word\}/g, remaining === 1 ? 'spot' : 'spots');

  return (
    <PublicShell>
      <MarketingHero
        eyebrow={eyebrow}
        title={heroHeadline}
        lede={heroLede}
        trailing={<FoundingCohortCounter />}
        // Only pass the photo once the CMS value is authoritative — on a
        // client-side navigation the injected blob is for the previous page, so
        // passing the default url here would flash the default before the
        // override loads. Until ready, the hero shows its radial backdrop.
        {...(heroReady ? { backgroundImage: heroImage.url } : {})}
        backgroundImageAlt={heroImage.alt}
        backgroundImageOpacity={heroImage.opacity ?? 1}
      />

      <MarketingSection eyebrow={offerEyebrow} title={offerTitle}>
        <Stack gap="lg">
          {offerItems.map((item, idx) => (
            <OfferRow key={item.title} item={item} index={idx + 1} />
          ))}
        </Stack>
      </MarketingSection>

      <MarketingSection eyebrow={mathEyebrow} title={mathTitle} tinted>
        <Card
          withBorder
          padding="xl"
          radius="lg"
          style={{
            backgroundColor: 'light-dark(rgba(255, 255, 255, 0.95), var(--mantine-color-dark-6))',
            borderColor: 'var(--mantine-color-ink-1)',
          }}
        >
          <Stack gap="xl">
            <Grid gap={{ base: 'md', md: 'xl' }}>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <PriceTile amount="$0" caption={priceCaptionYearOne} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <PriceTile amount={`${formatAud(monthlyAud)}`} caption={priceCaptionMonthly} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 4 }}>
                <PriceTile amount={`${formatAud(yearlyAud)}`} caption={yearlyCaption} />
              </Grid.Col>
            </Grid>
            <Divider color="ink.1" />
            <Text c="ink.7" size="lg" style={{ lineHeight: 1.6 }}>
              {mathPromise}
            </Text>
            <Box
              p="md"
              style={{
                backgroundColor: 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-5))',
                border: '1px solid var(--mantine-color-brand-2)',
                borderRadius: 8,
              }}
            >
              <Text c="ink.8" size="sm" style={{ lineHeight: 1.55 }}>
                {referralCallout}
              </Text>
            </Box>
          </Stack>
        </Card>
      </MarketingSection>

      <MarketingSection eyebrow={reciprocityEyebrow} title={reciprocityTitle}>
        <List spacing="md" size="lg" style={{ lineHeight: 1.6 }}>
          {reciprocityList.map((item) => (
            <List.Item key={item}>{item}</List.Item>
          ))}
        </List>

        {fullyClaimed ? (
          <Text size="sm" c="ink.7" mt="xl" style={{ lineHeight: 1.55, maxWidth: 620 }}>
            {claimedNote}
          </Text>
        ) : null}

        {/* The "Read the promise" secondary link was removed here (feedback
            ZUpiVILvIUfpok1OJGKsx) — the Promise page already sits in the
            "Before you sign up" tiles below, so this was a duplicate. */}
        <Group mt={fullyClaimed ? 'sm' : 'xl'} gap="md">
          {/* Mantine Button polymorphic-as-Link erases TanStack's typed
              search generics; wrap a typed Link around a presentational
              Button so the role-prefill survives the navigation. */}
          <JoinLink audience="host">
            {/* Tide gradient. This is the venue offer page's single conversion
                CTA (role=host), so it carries the venue colour like every other
                host-facing CTA across the marketing site. */}
            <Button
              component="span"
              tabIndex={-1}
              size="lg"
              variant="gradient"
              gradient={{ from: 'tide.7', to: 'tide.6', deg: 35 }}
            >
              {fullyClaimed ? ctaButtonClaimed : ctaButtonDefault}
            </Button>
          </JoinLink>
        </Group>
      </MarketingSection>

      <MarketingNextSteps
        title={nextStepsTitle}
        destinations={['/our-ethos', '/how-it-works']}
      />
    </PublicShell>
  );
}

function OfferRow({
  item,
  index,
}: {
  item: { title: string; body: string };
  index: number;
}) {
  return (
    <Group align="center" wrap="nowrap" gap="lg">
      <Box
        aria-hidden
        style={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background:
            'linear-gradient(135deg, var(--mantine-color-brand-1), var(--mantine-color-sun-1))',
          color: 'var(--mantine-color-brand-7)',
          fontWeight: 700,
          fontSize: 14,
        }}
      >
        {String(index).padStart(2, '0')}
      </Box>
      <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
        <Text fw={600} size="lg" c="ink.9" style={{ lineHeight: 1.35 }}>
          {item.title}
        </Text>
        {item.body.trim() !== '' ? (
          <Text c="ink.7" style={{ lineHeight: 1.55 }}>
            {item.body}
          </Text>
        ) : null}
      </Stack>
    </Group>
  );
}

function PriceTile({ amount, caption }: { amount: string; caption: string }) {
  return (
    <Stack gap={6}>
      <Text
        component="span"
        style={{
          fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
          lineHeight: 1.1,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: 'var(--mantine-color-ink-9)',
        }}
      >
        {amount}
      </Text>
      <Text c="ink.6" size="sm" style={{ lineHeight: 1.55 }}>
        {caption}
      </Text>
    </Stack>
  );
}

